import React from 'react';
import { Control, FieldValues, Path } from 'react-hook-form';
import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { ColumnFieldDefinition } from '@/services/tableRowService';

interface DynamicFormFieldProps<T extends FieldValues> {
  column: ColumnFieldDefinition;
  control: Control<T>;
  fieldName: Path<T>;
}

/**
 * Dynamically renders a form field based on column definition
 */
export function DynamicFormField<T extends FieldValues>({
  column,
  control,
  fieldName,
}: DynamicFormFieldProps<T>) {
  return (
    <FormField
      control={control}
      name={fieldName}
      render={({ field }) => (
        <FormItem>
          <FormLabel>
            {column.label}
            {column.required && <span className="text-destructive ml-1">*</span>}
          </FormLabel>
          <FormControl>
            {column.type === 'select' && column.options ? (
              <Select
                onValueChange={(value) => {
                  field.onChange(value);
                  // Also set statusColor if this is a status field
                  if (column.name.toLowerCase().includes('status')) {
                    const option = column.options?.find((opt) => opt.value === value);
                    if (option?.color && control._formValues) {
                      (control._formValues as Record<string, unknown>).statusColor = option.color;
                    }
                  }
                }}
                defaultValue={field.value as string}
              >
                <SelectTrigger>
                  <SelectValue placeholder={`Select ${column.label.toLowerCase()}`} />
                </SelectTrigger>
                <SelectContent>
                  {column.options.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      <div className="flex items-center gap-2">
                        {option.color && (
                          <div
                            className="w-3 h-3 rounded-full"
                            style={{ backgroundColor: option.color }}
                          />
                        )}
                        {option.label}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            ) : column.type === 'date' ? (
              <Input
                type="date"
                placeholder={`Enter ${column.label.toLowerCase()}`}
                {...field}
                value={field.value as string}
              />
            ) : column.type === 'hidden' ? (
              <Input type="hidden" {...field} value={field.value as string} />
            ) : (
              <Input
                type="text"
                placeholder={`Enter ${column.label.toLowerCase()}`}
                {...field}
                value={field.value as string}
              />
            )}
          </FormControl>
          {column.type === 'date' && (
            <FormDescription className="text-xs">
              Format: YYYY-MM-DD
            </FormDescription>
          )}
          <FormMessage />
        </FormItem>
      )}
    />
  );
}
